import { Alert, Button, Group, Modal, Pagination, ScrollArea, Stack, Table, Text, TextInput } from '@mantine/core';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useFunctions } from '../helpers/firebaseContext';
import { KfhtradeHistoryRequest, KfhtradeHistoryResult } from '../types';

export default function KfhtradeBatchHistory({ opened, onClose }: { opened: boolean; onClose: () => void }) {
    const functions = useFunctions();
    functions.region = 'europe-west1';
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [revision, setRevision] = useState(0);
    const [result, setResult] = useState<KfhtradeHistoryResult>({ rows: [], found: 0 });
    const [loading, setLoading] = useState(false);
    const [indexing, setIndexing] = useState(false);
    const [indexed, setIndexed] = useState(0);
    const [error, setError] = useState('');
    const call = (data: KfhtradeHistoryRequest) => httpsCallable<KfhtradeHistoryRequest, KfhtradeHistoryResult>(functions, 'kfhtradeBatchHistory', { timeout: 120000 })(data);
    useEffect(() => {
        if (!opened) return;
        let cancelled = false;
        setLoading(true); setError('');
        void call({ action: 'search', query: search, page }).then(response => {
            if (!cancelled) setResult(response.data);
        }).catch(err => { if (!cancelled) setError(err.message || 'Could not search history'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    // Search only on explicit submission, page changes, or refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, search, page, revision]);
    const reindex = async () => {
        setIndexing(true); setIndexed(0); setError('');
        try {
            let cursor: string | undefined;
            do {
                const response = await call({ action: 'reindex', ...(cursor ? { cursor } : {}) });
                setIndexed(count => count + (response.data.indexed || 0));
                cursor = response.data.nextCursor;
            } while (cursor);
            setRevision(value => value + 1);
        } catch (err: any) { setError(err.message || 'Could not refresh history index'); }
        finally { setIndexing(false); }
    };
    const date = (seconds: number) => seconds ? new Date(seconds * 1000).toLocaleString('en-GB', { timeZone: 'Asia/Kuwait' }) : '—';
    return <Modal opened={opened} onClose={onClose} title="KFHTrade batch history" size="xl" closeOnClickOutside={!indexing} closeOnEscape={!indexing} withCloseButton={!indexing}>
        <Stack>
            <Text size="sm">Search past updates by client name, customer ID, KFHTrade ID, or batch ID. Search results may briefly lag behind live progress. Times are in Kuwait time.</Text>
            <form onSubmit={event => { event.preventDefault(); setPage(1); setSearch(query); setRevision(value => value + 1); }}>
                <Group><TextInput aria-label="Search batch history" placeholder="Client, customer ID, KFHTrade ID, or batch ID" value={query} onChange={event => setQuery(event.currentTarget.value)} style={{ flex: 1 }} /><Button type="submit" loading={loading}>Search</Button></Group>
            </form>
            <Group><Button variant="light" onClick={() => { void reindex(); }} loading={indexing}>Refresh search history</Button><Text size="xs">Includes existing records; does not resend updates or messages.</Text></Group>
            {!!indexed && <Text size="sm">{indexed} records indexed{indexing ? '…' : '.'}</Text>}
            {error && <Alert color="red">{error}</Alert>}
            <Text size="sm">{loading ? 'Loading…' : `${result.found} records found`}</Text>
            <ScrollArea><Table miw={950}><Table.Thead><Table.Tr>{['Client / customer ID', 'KFHTrade ID', 'Batch', 'Created', 'DFN status', 'DFN updated', 'WhatsApp status', 'WhatsApp sent'].map(title => <Table.Th key={title}>{title}</Table.Th>)}</Table.Tr></Table.Thead><Table.Tbody>
                {result.rows.map(row => <Table.Tr key={row.id}><Table.Td>{row.clientName || row.customerID}</Table.Td><Table.Td>{row.customerNo}</Table.Td><Table.Td>{row.batchID}</Table.Td><Table.Td>{date(row.createdAt)}</Table.Td><Table.Td>{row.status}</Table.Td><Table.Td>{date(row.dfnUpdatedAt)}</Table.Td><Table.Td>{row.whatsappStatus}</Table.Td><Table.Td>{date(row.whatsappSentAt)}</Table.Td></Table.Tr>)}
            </Table.Tbody></Table></ScrollArea>
            <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(result.found / 25))} disabled={loading} />
        </Stack>
    </Modal>;
}
